import { useApolloClient } from '@apollo/client/react';
import { useCallback } from 'react';

import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useRevokeTwaDeviceToken } from '#src/hooks/useTwaDeviceToken.ts';
import { AuthBroadCastChannel } from '#src/lib/broadcastChannel.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { forgetFlagsContext } from '#src/lib/featureFlags.ts';
import {
  forgetSignedInUser,
  rememberSignedInUser,
} from '#src/lib/gql/signedInUser.ts';
import { client as httpClient, type HttpResponse } from '#src/lib/http.ts';

import { goToRoot } from './goToRoot.ts';

export const useLogout = () => {
  const client = useApolloClient();
  const track = useEvents();
  const { isMobileApp, postMessage: mobileAppPostMessage } = useIsMobileApp();
  const revokeTwaDeviceToken = useRevokeTwaDeviceToken();

  return useCallback(async () => {
    // the sign out reloads the page: the next visit must start logged out
    forgetFlagsContext();
    // needs the session: must settle before the sign out request. Failures are
    // reported by the mutation and never block the logout.
    await revokeTwaDeviceToken().catch(() => {});

    // Not clearStore: it cancels in-flight queries (Apollo error 92), which
    // suspense queries throw into the error boundary. Forgetting the user is
    // enough to stop their UNAUTHORIZED from bouncing to sign-in once the
    // session is gone.
    forgetSignedInUser(client);

    return httpClient.delete('/users/sign_out.json').then(
      response => {
        if (response.status === 204) {
          track('[Client] Log Out');
          if (window.analytics) {
            window.analytics.reset();
          }
        }

        goToRoot();

        // log out all tabs
        AuthBroadCastChannel.postMessage('logged_out');

        // log out Flutter webview-based app
        if (isMobileApp) {
          mobileAppPostMessage('logout', { isExplicit: true });
        }

        return response;
      },
      (error: Error) => {
        rememberSignedInUser(client);
        return { error: error.message };
      }
    );
  }, [client, isMobileApp, mobileAppPostMessage, revokeTwaDeviceToken, track]);
};

export type { HttpResponse };
